import { LoadingButton } from '@mui/lab';
import { Box, Button, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuthContext } from 'src/auth/hooks';
import { LoadingScreen } from 'src/components/loading-screen';
import { useSettingsContext } from 'src/components/settings';
import { getClinicDetails } from 'src/services/clinic.service';

export default function ClinicBusinessProfileView() {
  const settings = useSettingsContext();
  const { user } = useAuthContext();
  const [clinicData, setClinicData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user?.clinic_id) return;

    const fetchClinic = async () => {
      setIsLoading(true);
      try {
        const data = await getClinicDetails(user.clinic_id);
        const mappedData = {
          ...data,
          address: {
            city: data.address?.city || '',
            state: data.address?.state || '',
            zip_code: data.address?.zip_code || '',
            country: data.address?.country || 'India',
          },
          pan: data.pan || data.pan_number || '',
          institute_category: data.institute_category || [],
          services_provided: data.services_provided || [],
          available_days: data.available_days || [],
          logo_image: data.logo_image || '',
        };
        setClinicData(mappedData);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClinic();
  }, [location.key, user?.clinic_id]); // Refetch whenever route changes or user.clinic_id changes

  const ClinicInfoItem = ({ title, value }) => (
    <div className="flex-grow">
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body1">{value}</Typography>
    </div>
  );
  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <Stack direction="row" justifyContent="space-between" sx={{ marginBottom: '14px' }}>
        <Typography variant="h4">Clinic Business Profile</Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/clinic-dashboard/business-profile/edit')}
        >
          Edit Profile
        </Button>
      </Stack>

      {isLoading ? (
        <Box sx={{ minHeight: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <LoadingScreen />
        </Box>
      ) : (
        clinicData && (
          <Stack direction="row" gap="14px" flexWrap="wrap">
            <Grid container spacing={{ xs: '14px', lg: '44px' }}>
              <Grid item xs={12} sm={4}>
                <ClinicInfoItem title="Brand" value={clinicData.brand} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClinicInfoItem title="Legal Name" value={clinicData.legal_name} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClinicInfoItem title="Unique Id" value={clinicData.unique_id} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClinicInfoItem
                  title="Registration Number"
                  value={clinicData.registration_number || 'To be entered'}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClinicInfoItem
                  title="GST Number"
                  value={clinicData.gst_number || 'To be entered'}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClinicInfoItem
                  title="PAN Number"
                  value={clinicData.pan || 'To be entered'}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClinicInfoItem
                  title="Google Business Profile"
                  value={clinicData.google_business_profile_url || 'To be entered'}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClinicInfoItem title="Email" value={clinicData.email || 'To be entered'} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ClinicInfoItem title="Phone" value={clinicData.phone || 'To be entered'} />
              </Grid>
              <Grid item xs={12}>
                <ClinicInfoItem
                  title="Address"
                  value={`${clinicData.address.city}, ${clinicData.address.state}, ${clinicData.address.zip_code}, ${clinicData.address.country}`}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Brand Logo</Typography>
                {clinicData.logo_image ? (
                  <img src={clinicData.logo_image} alt="brand-logo" />
                ) : (
                  <Typography>To be Uploaded</Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Institute Category</Typography>
                {clinicData.institute_category.length > 0 ? (
                  <Stack direction="row" gap={2}>
                    {clinicData.institute_category.map((inst, idx) => (
                      <Chip key={idx} label={inst} />
                    ))}
                  </Stack>
                ) : (
                  <Typography>To be added</Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Available Days</Typography>
                {clinicData.available_days.length > 0 ? (
                  <Stack direction="row" gap={2}>
                    {clinicData.available_days.map((day, idx) => (
                      <Chip key={idx} label={day} />
                    ))}
                  </Stack>
                ) : (
                  <Typography>To be added</Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Services Provided</Typography>
                {clinicData.services_provided.length > 0 ? (
                  <Stack direction="row" gap={2}>
                    {clinicData.services_provided.map((service, idx) => (
                      <Chip key={idx} label={service} />
                    ))}
                  </Stack>
                ) : (
                  <Typography>To be added</Typography>
                )}
              </Grid>
            </Grid>
          </Stack>
        )
      )}
    </Container>
  );
}
